import { useContext, useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';

import { ApiContext } from '../Api/Api';
import { URLState, URLStateContext } from '../URLState/URLState';
import appendURLSearchParams from '../../utils/appendURLSearchParams';
import getFormattedFilters from '../../utils/getFormattedFilters';
import getDateRange from '../../utils/time/getTimeRange';

export interface SessionIDs {
  current: null | string;
  next: null | string;
  previous: null | string;
}

type HookPayload = {
  ids: SessionIDs;
  isEmpty: boolean;
  isFetched: boolean;
  offset: number;
  total: number;
};

type HookResult = Pick<UseQueryResult, 'isError' | 'isFetching' | 'refetch'> & HookPayload;

const useSessions = (session: string | null, urlStateOverrides?: Partial<URLState>): HookResult => {
  const { get } = useContext(ApiContext);
  const { dateFrom, dateTo, demo, duration, filters } = {
    ...useContext(URLStateContext),
    ...urlStateOverrides,
  };
  const sessionId = session ? session.toString() : undefined;
  const { dateFrom: start, dateTo: end } = getDateRange(duration, dateFrom, dateTo);
  const [payload, setPayload] = useState<Omit<HookPayload, 'isFetched'>>({
    ids: {
      current: null,
      next: null,
      previous: null,
    },
    isEmpty: false,
    offset: 0,
    total: 0,
  });

  const { data, isError, isFetching, refetch, isSuccess } = useQuery<{
    ids: SessionIDs[];
    offset: number;
    total: number;
  }>(['sessions', { demo, get, end, filters, sessionId, start }], async () =>
    get(
      `/sessions?${appendURLSearchParams(
        '',
        ['filter', getFormattedFilters(filters)],
        ['session', sessionId],
        ['size', '1'],
        ['start', start.toString()],
        ['end', end.toString()],
      )}`,
    ),
  );

  /**
   * Use a side-effect to:
   *  - Update `hookPayload` state, whenever the sessions query resolves
   */
  useEffect(() => {
    if (data) {
      const {
        ids: [
          firstIdSet = {
            current: null,
            next: null,
            previous: null,
          },
        ],
        offset,
        total,
      } = data;

      setPayload({
        ids: {
          current: firstIdSet.current,
          next: firstIdSet.next,
          previous: firstIdSet.previous,
        },
        isEmpty: firstIdSet.current === null,
        offset,
        total,
      });
    }
  }, [data]);

  return {
    isError,
    isFetched: isSuccess,
    isFetching,
    refetch,
    ...payload,
  };
};

export default useSessions;
